import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/auth/AuthContext';
import { hydrateSeenFromServer } from '@/hooks/useSeen';
import { hydratePrefsFromServer } from '@/hooks/useSyncedPref';
import { parseSseChunk } from '@/lib/sse';

/**
 * Real-time portal sync: one Server-Sent Events stream (`GET /api/live`) that
 * every open tab holds. When the server announces a change ("notes", "links",
 * "seen"…), the matching React Query caches refresh IMMEDIATELY instead of
 * waiting for their next poll. Payloads carry no data, just the change kind.
 *
 * Read with fetch() rather than EventSource: the session cookie rides along
 * either way, but fetch goes through the auth wrapper, which renews an expired
 * session before the reconnect gives up.
 */
export function useLiveSync(myEmail: string): void {
  const qc = useQueryClient();
  const { authConfigured } = useUser();
  const retry = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!authConfigured) return;
    const email = myEmail.toLowerCase();

    const inv = (...prefixes: string[]) =>
      prefixes.forEach((p) => void qc.invalidateQueries({ queryKey: [p] }));

    /* Everything that is "live" in the portal, so a reconnect can catch up on
       what it missed while the stream was down. */
    const LIVE_KEYS = [
      'my-notifications', 'ws-notes', 'ws-notes-range', 'ws-notes-ticket', 'ws-tagged-notes',
      'ws-worktasks', 'ws-ticket-tasks', 'ws-handover-mine', 'ws-handover-assigned',
      'custom-links', 'portal-resources', 'admin-resources', 'case-log', 'portal-config',
    ];
    const catchUp = () => LIVE_KEYS.forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));

    const handle = (event: string, payload: Record<string, unknown>) => {
      const by = String(payload.by || '').toLowerCase();
      switch (event) {
        case 'notes':
          inv('ws-notes', 'ws-notes-range', 'ws-notes-ticket', 'ws-tagged-notes');
          break;
        case 'tasks':
          inv('ws-worktasks', 'ws-ticket-tasks', 'ws-worktasks-trash');
          break;
        case 'handover':
          inv('ws-handover-mine', 'ws-handover-assigned', 'ws-handover-team', 'ws-handover', 'ws-handover-worker', 'ws-handover-absences');
          break;
        case 'links':
          inv('custom-links', 'admin-pending-links', 'my-link-requests');
          break;
        case 'resources':
          inv('portal-resources', 'admin-resources', 'my-resource-requests');
          break;
        case 'portal-config':
          inv('portal-config', 'custom-links');
          break;
        case 'accounts':
          inv('admin-pending-accounts', 'dyn-team');
          break;
        case 'errors':
          inv('portal-errors-notif');
          break;
        case 'notif':
          inv('my-notifications');
          break;
        case 'case-log':
          inv('case-log', 'ticket-snoozes');
          break;
        // Personal state: only MY other sessions need to react.
        case 'seen':
          if (by === email) void hydrateSeenFromServer();
          break;
        case 'prefs':
          if (by === email) void hydratePrefsFromServer();
          break;
      }
    };

    /* `alive` stops a timer that fires after unmount from opening a stream
       nobody will ever close; `gen` makes a superseded stream's ending read as
       what it is, not as "the connection dropped" (which would reopen again). */
    let alive = true;
    let gen = 0;
    let open = false;
    let abort: AbortController | null = null;

    /* Backoff: 4s, 8s, 16s … up to a minute, reset once a stream opens.
       A flat four seconds against an outage meant a reconnect — and fourteen
       cache invalidations — every four seconds, in every open tab. */
    let attempt = 0;
    const reopen = () => {
      if (retry.current) clearTimeout(retry.current);
      const wait = Math.min(60_000, 4000 * 2 ** attempt);
      attempt += 1;
      retry.current = setTimeout(() => {
        if (!alive) return;
        connect();
        if (document.visibilityState === 'visible') catchUp();
      }, wait);
    };

    const connect = () => {
      if (!alive) return;
      const mine = ++gen;
      abort?.abort();
      const ctrl = new AbortController();
      abort = ctrl;
      void (async () => {
        try {
          const r = await fetch('/api/live', { credentials: 'same-origin', signal: ctrl.signal, cache: 'no-store' });
          if (!r.ok || !r.body) throw new Error(`live ${r.status}`);
          open = true;
          attempt = 0;
          const reader = r.body.pipeThrough(new TextDecoderStream()).getReader();
          let buffer = '';
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            const parsed = parseSseChunk(buffer + value);
            buffer = parsed.rest;
            for (const ev of parsed.events) handle(ev.event, ev.data);
          }
        } catch { /* aborted, offline or refused — handled below */ }
        if (mine !== gen) return;
        open = false;
        if (alive) reopen();
      })();
    };

    connect();

    /* Coming back to the tab (or back online) catches up on what was missed,
       and a stream that died while the tab slept is replaced. */
    const wake = () => {
      if (document.visibilityState !== 'visible') return;
      catchUp();
      if (!open) connect();
    };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);

    return () => {
      alive = false;
      gen++;
      if (retry.current) clearTimeout(retry.current);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('online', wake);
      abort?.abort();
    };
  }, [myEmail, qc, authConfigured]);
}
